import api from './api';

const entryGuideService = {
  /**
   * Obtener todas las guías de entrada
   */
  getEntryGuides: async (params = {}) => {
    try {
      const response = await api.get('/entry-guides', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener guías de entrada:', error);
      throw error;
    }
  },

  /**
   * Obtener una guía de entrada por ID
   */
  getEntryGuide: async (id, params = {}) => {
    try {
      const response = await api.get(`/entry-guides/${id}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener guía de entrada:', error);
      throw error;
    }
  },

  /**
   * Crear una nueva guía de entrada
   */
  createEntryGuide: async (entryGuideData) => {
    try {
      const response = await api.post('/entry-guides', entryGuideData);
      return response.data;
    } catch (error) {
      console.error('Error al crear guía de entrada:', error);
      throw error;
    }
  },

  /**
   * Actualizar una guía de entrada
   */
  updateEntryGuide: async (id, entryGuideData) => {
    try {
      const response = await api.put(`/entry-guides/${id}`, entryGuideData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar guía de entrada:', error);
      throw error;
    }
  },

  /**
   * Eliminar una guía de entrada
   */
  deleteEntryGuide: async (id) => {
    try {
      const response = await api.delete(`/entry-guides/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar guía de entrada:', error);
      throw error;
    }
  },

  /**
   * Obtener órdenes de compra pendientes (para agregar a guía)
   */
  getPendingPurchaseOrders: async (params = {}) => {
    try {
      const response = await api.get('/purchase-orders', { 
        params: { ...params, status: 'issued' } 
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener órdenes de compra pendientes:', error);
      throw error;
    }
  },

  /**
   * Obtener documentos pendientes (para agregar a guía)
   */
  getPendingDocuments: async (params = {}) => {
    try {
      const response = await api.get('/documents', { 
        params: { ...params, status: 'issued', only_pending: 'true' } 
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener documentos pendientes:', error);
      throw error;
    }
  },

  /**
   * Obtener stock y costo de un producto en un almacén
   */
  getProductStockAndCost: async (warehouseId, productId) => {
    try {
      const response = await api.get('/entry-guides/product/stock-cost', {
        params: { warehouse_id: warehouseId, product_id: productId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener stock y costo del producto:', error);
      throw error;
    }
  },

  /**
   * Obtener stock y costo de todos los productos en un almacén (una llamada).
   * Si excludeEntryGuideId está definido (al editar), el backend excluye los movimientos de esa guía.
   * Si asOfDate/asOfTime están definidos (ej. al editar), el backend solo considera movimientos
   * estrictamente anteriores a ese momento (fecha + hora), es decir, el inventario justo antes de esa guía.
   */
  getProductStockAndCostBulk: async (warehouseId, excludeEntryGuideId = null, asOfDate = null, asOfTime = null, includeAllActiveProducts = false) => {
    try {
      const params = { warehouse_id: warehouseId };
      if (excludeEntryGuideId) params.exclude_entry_guide_id = excludeEntryGuideId;
      if (asOfDate) params.as_of_date = asOfDate;
      if (asOfTime != null && asOfTime !== '') params.as_of_time = asOfTime;
      if (includeAllActiveProducts) params.include_all_active_products = '1';
      const response = await api.get('/entry-guides/product/stock-cost-bulk', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener stock/costo por almacén (bulk):', error);
      throw error;
    }
  },

  getLastPurchaseCostBulk: async (productIds) => {
    try {
      const ids = Array.isArray(productIds) ? productIds.filter(Boolean).join(',') : String(productIds || '');
      const response = await api.get('/entry-guides/product/last-purchase-cost-bulk', {
        params: { product_ids: ids },
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener última compra global:', error);
      throw error;
    }
  },

  /**
   * Obtener guías de traspaso pendientes (sin recepción asociada) para recepciones
   */
  getPendingTransferGuides: async (warehouseId) => {
    try {
      const response = await api.get('/entry-guides/pending-transfer-guides', {
        params: { 
          warehouse_id: warehouseId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener guías de traspaso pendientes:', error);
      throw error;
    }
  },

  /**
   * Recalcular y actualizar guías basado en movimientos recalculados
   */
  recalculateGuides: async (movements) => {
    try {
      const response = await api.post('/entry-guides/recalculate', {
        movements: movements
      });
      return response.data;
    } catch (error) {
      console.error('Error al recalcular guías:', error);
      throw error;
    }
  },

  /**
   * Catálogos livianos del formulario de guías.
   */
  getFormData: async (warehouseId = null) => {
    try {
      const params = {};
      if (warehouseId) params.warehouse_id = warehouseId;
      const response = await api.get('/entry-guides/form-data', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener datos del formulario de guías:', error);
      throw error;
    }
  },

  /**
   * Referencias pendientes para el modal "Seleccionar referencias".
   */
  getFormReferences: async (params = {}) => {
    try {
      const response = await api.get('/entry-guides/form-references', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener referencias del formulario de guías:', error);
      throw error;
    }
  },

  /**
   * Obtener guías de salida de consumos por proveedor y producto
   */
  getConsumptionExitGuides: async (supplierId, productId) => {
    try {
      const response = await api.get('/entry-guides/consumption-exit-guides', {
        params: { supplier_id: supplierId, product_id: productId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener guías de salida de consumos:', error);
      throw error;
    }
  },

  /**
   * Obtener guías de saldo inicial por producto y almacén
   */
  consumeReception: async (receptionGuideId, payload) => {
    try {
      const response = await api.post(`/entry-guides/${receptionGuideId}/consume-reception`, payload);
      return response.data;
    } catch (error) {
      console.error('Error al consumir recepción:', error);
      throw error;
    }
  },

  writeOffTransferPending: async (transferGuideId, payload = {}) => {
    try {
      const response = await api.post(`/entry-guides/${transferGuideId}/write-off-pending`, payload);
      return response.data;
    } catch (error) {
      console.error('Error al dar por perdido traspaso pendiente:', error);
      throw error;
    }
  },

  revokeTransferWriteOff: async (transferGuideId) => {
    try {
      const response = await api.delete(`/entry-guides/${transferGuideId}/write-off-pending`);
      return response.data;
    } catch (error) {
      console.error('Error al revocar baja por pérdida de traspaso:', error);
      throw error;
    }
  },

  getInitialBalanceGuides: async (productId, warehouseId = null) => {
    try {
      const params = { product_id: productId };
      if (warehouseId) {
        params.warehouse_id = warehouseId;
      }
      const response = await api.get('/entry-guides/initial-balance-guides', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener guías de saldo inicial:', error);
      throw error;
    }
  },
};

export default entryGuideService;


